import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, Truck, AlertCircle, ExternalLink, CheckCircle2 } from 'lucide-react';
import { purchaseOrdersService, purchaseOrderItemsService, componentInventoryService, locationsService } from '@/lib/services';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/contexts/AuthContext';

interface PONeedingReceive {
  id: string;
  po_number: string;
  locationName: string | null;
  itemsToReceive: number;
  totalItems: number;
}

interface InventoryNeedingShip {
  locationId: string;
  locationName: string;
  itemCount: number;
}

export function MyActionItems() {
  const { user } = useAuth();
  
  // Only show for john@thepeakbeyond.com
  if (user?.email !== 'john@thepeakbeyond.com') {
    return null;
  }

  // Fetch POs with "purchased" status
  const { data: purchasedPOs = [] } = useQuery({
    queryKey: ['action-items', 'purchased-pos'],
    queryFn: async () => {
      const allPOs = await purchaseOrdersService.getAll();
      return allPOs.filter(po => po.status === 'purchased');
    },
  });

  // Fetch locations for name lookup
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsService.getAll(),
  });

  // Fetch all inventory to check which PO items have been received
  const { data: allInventory = [] } = useQuery({
    queryKey: ['component-inventory'],
    queryFn: () => componentInventoryService.getAll(),
  });

  // Fetch all PO items for purchased POs
  const poIds = purchasedPOs.map(po => po.id);
  const { data: allPOItems = [] } = useQuery({
    queryKey: ['action-items', 'po-items', poIds.join(',')],
    queryFn: async () => {
      if (poIds.length === 0) return [];
      const results = await Promise.all(
        poIds.map(id => purchaseOrderItemsService.getByPurchaseOrderId(id))
      );
      return results.flat();
    },
    enabled: poIds.length > 0,
  });

  // Calculate POs needing items to be received
  const posNeedingReceive: PONeedingReceive[] = purchasedPOs
    .map(po => {
      const poItems = allPOItems.filter(item => item.purchase_order_id === po.id);
      const totalItems = poItems.length;
      
      // Count items that have inventory records (meaning they've been received)
      const receivedItemComponentIds = new Set(
        allInventory
          .filter(inv => inv.purchase_order_id === po.id)
          .map(inv => inv.component_id)
      );
      
      // Items without component_id can't be tracked in inventory, so exclude them
      const trackableItems = poItems.filter(item => item.component_id);
      const itemsToReceive = trackableItems.filter(
        item => !receivedItemComponentIds.has(item.component_id!)
      ).length;

      const location = po.location_id 
        ? locations.find(loc => loc.id === po.location_id)
        : null;

      return {
        id: po.id,
        po_number: po.po_number,
        locationName: location?.location_name || null,
        itemsToReceive,
        totalItems,
      };
    })
    .filter(po => po.itemsToReceive > 0);

  // Calculate inventory items needing to be shipped
  // Items that are "received" status, allocated to a location, but not shipped
  const inventoryNeedingShip = allInventory.filter(
    inv => 
      inv.assignment_status === 'received' &&
      inv.assigned_location_id !== null &&
      inv.ship_date === null
  );

  // Group by location
  const shipByLocation: InventoryNeedingShip[] = [];
  const locationCounts: Record<string, number> = {};
  
  inventoryNeedingShip.forEach(inv => {
    if (inv.assigned_location_id) {
      locationCounts[inv.assigned_location_id] = (locationCounts[inv.assigned_location_id] || 0) + inv.quantity;
    }
  });

  Object.entries(locationCounts).forEach(([locationId, itemCount]) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      shipByLocation.push({
        locationId,
        locationName: location.location_name,
        itemCount,
      });
    }
  });

  const totalPOsToReceive = posNeedingReceive.length;
  const totalItemsToShip = Object.values(locationCounts).reduce((a, b) => a + b, 0);
  const totalLocationsToShip = shipByLocation.length;

  // If no action items, show a nice empty state
  if (totalPOsToReceive === 0 && totalLocationsToShip === 0) {
    return (
      <Card className="border-[#5ac59a]/30 bg-[#5ac59a]/5">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[#5ac59a]" />
            <CardTitle className="text-lg">My Action Items</CardTitle>
          </div>
          <CardDescription>You're all caught up! No pending actions.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-lg">My Action Items</CardTitle>
          </div>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
            {totalPOsToReceive + totalLocationsToShip} pending
          </Badge>
        </div>
        <CardDescription>Items requiring your attention</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* POs Needing Items Received */}
        {posNeedingReceive.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package className="h-4 w-4" />
              <span>Purchase Orders to Receive ({totalPOsToReceive})</span>
            </div>
            <div className="space-y-2 pl-6">
              {posNeedingReceive.slice(0, 5).map(po => (
                <div 
                  key={po.id}
                  className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{po.po_number}</span>
                      {po.locationName && (
                        <span className="text-xs text-gray-500 truncate">
                          → {po.locationName}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-amber-600">
                      {po.itemsToReceive} of {po.totalItems} items need receiving
                    </span>
                  </div>
                  <Button 
                    asChild 
                    size="sm" 
                    variant="outline"
                    className="ml-2 shrink-0 text-xs h-7"
                  >
                    <Link to={ROUTES.PURCHASE_ORDER_DETAIL(po.id)}>
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Mark Received
                    </Link>
                  </Button>
                </div>
              ))}
              {posNeedingReceive.length > 5 && (
                <Link 
                  to={`${ROUTES.PURCHASE_ORDERS}?status=purchased`}
                  className="text-sm text-[#5ac59a] hover:underline"
                >
                  View all {posNeedingReceive.length} POs →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Inventory Needing Shipment */}
        {shipByLocation.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Truck className="h-4 w-4" />
              <span>Items Ready to Ship ({totalItemsToShip} items)</span>
            </div>
            <div className="space-y-2 pl-6">
              {shipByLocation.slice(0, 5).map(loc => (
                <div 
                  key={loc.locationId}
                  className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm truncate block">{loc.locationName}</span>
                    <span className="text-xs text-amber-600">
                      {loc.itemCount} item{loc.itemCount !== 1 ? 's' : ''} allocated, not yet shipped
                    </span>
                  </div>
                  <Button 
                    asChild 
                    size="sm" 
                    variant="outline"
                    className="ml-2 shrink-0 text-xs h-7"
                  >
                    <Link to={`${ROUTES.SHIPMENTS}/new?location_id=${loc.locationId}`}>
                      <Truck className="h-3 w-3 mr-1" />
                      Create Shipment
                    </Link>
                  </Button>
                </div>
              ))}
              {shipByLocation.length > 5 && (
                <Link 
                  to={ROUTES.INVENTORY_MANAGEMENT}
                  className="text-sm text-[#5ac59a] hover:underline"
                >
                  View all locations →
                </Link>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Hook to get action item counts for sidebar badges
export function useActionItemCounts() {
  const { user } = useAuth();
  
  // Only calculate for john@thepeakbeyond.com
  const enabled = user?.email === 'john@thepeakbeyond.com';

  // Fetch POs with "purchased" status
  const { data: purchasedPOs = [] } = useQuery({
    queryKey: ['action-items', 'purchased-pos'],
    queryFn: async () => {
      const allPOs = await purchaseOrdersService.getAll();
      return allPOs.filter(po => po.status === 'purchased');
    },
    enabled,
  });

  // Fetch all inventory
  const { data: allInventory = [] } = useQuery({
    queryKey: ['component-inventory'],
    queryFn: () => componentInventoryService.getAll(),
    enabled,
  });

  // Fetch all PO items for purchased POs
  const poIds = purchasedPOs.map(po => po.id);
  const { data: allPOItems = [] } = useQuery({
    queryKey: ['action-items', 'po-items', poIds.join(',')],
    queryFn: async () => {
      if (poIds.length === 0) return [];
      const results = await Promise.all(
        poIds.map(id => purchaseOrderItemsService.getByPurchaseOrderId(id))
      );
      return results.flat();
    },
    enabled: enabled && poIds.length > 0,
  });

  if (!enabled) {
    return { posToReceive: 0, itemsToShip: 0 };
  }

  // Count POs needing items to be received
  const receivedItemsByPO: Record<string, Set<string>> = {};
  allInventory.forEach(inv => {
    if (inv.purchase_order_id && inv.component_id) {
      const receivedSet = receivedItemsByPO[inv.purchase_order_id] ?? new Set<string>();
      receivedSet.add(inv.component_id);
      receivedItemsByPO[inv.purchase_order_id] = receivedSet;
    }
  });

  const posToReceive = purchasedPOs.filter(po => {
    const poItems = allPOItems.filter(item => item.purchase_order_id === po.id && item.component_id);
    const receivedSet = receivedItemsByPO[po.id] || new Set();
    return poItems.some(item => !receivedSet.has(item.component_id!));
  }).length;

  // Count unique locations with items to ship
  const locationsWithItemsToShip = new Set(
    allInventory
      .filter(inv => 
        inv.assignment_status === 'received' &&
        inv.assigned_location_id !== null &&
        inv.ship_date === null
      )
      .map(inv => inv.assigned_location_id)
  ).size;

  return { 
    posToReceive, 
    itemsToShip: locationsWithItemsToShip 
  };
}
